import React, {
    useEffect, useState, useCallback, useRef,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { authManager } from '../utils/auth';
import { isNonMmlAdmin } from '../utils/roles';
import MemberSearchField, { memberDisplayName } from '../Components/MemberSearchField';
import NotAvailable from '../Components/NotAvailable';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

// Totals and averages across loyalty receipts (somo-circle1-api-admin#188,
// API: somo-circle1-api#784). CircleOne-only, like the per-receipt line
// items it summarizes: the API route is behind requireNonMmlAdmin.

export const STATUS_OPTIONS = [
    { value: 'accepted', label: 'Accepted' },
    { value: 'all', label: 'All' },
    { value: 'submitted', label: 'Submitted (queue)' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'needs_resubmission', label: 'Needs a better photo' },
];

// Sent as commissaryBaseId/state to select receipts with no matched
// commissary / no known state.
const UNKNOWN = 'unknown';

// Rows per page in each table. The tables page through what the API
// returned, so the ranked lists are fetched at their API maximums.
export const PAGE_SIZE = 10;
const TOP_PRODUCTS = 100;
const TOP_MEMBERS = 500;
export const NO_ROWS = [];

const EMPTY_FILTERS = {
    from: '', to: '', commissaryBaseId: '', state: '', member: null, status: 'accepted', granularity: 'month', tender: '',
};

// "Paid with" (somo-circle1-api#799): sent as tender. SNAP is the reviewer's
// figure once reviewed; WIC is any sign of WIC on the receipt.
export const TENDER_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'snap', label: 'SNAP' },
    { value: 'wic', label: 'WIC' },
    { value: 'snap_or_wic', label: 'SNAP or WIC' },
    { value: 'neither', label: 'Neither' },
];
export const tenderLabel = (tender) => TENDER_OPTIONS.find((o) => o.value === tender)?.label || tender;

// The trend's period (somo-circle1-api#787). Month is the API default, so
// it's left out of the request.
export const GRANULARITY_OPTIONS = [
    { value: 'month', label: 'Month' },
    { value: 'week', label: 'Week' },
    { value: 'day', label: 'Day' },
];
// The API's limit for the day view.
const MAX_DAY_BUCKETS = 400;
const DAY_MS = 24 * 60 * 60 * 1000;
export const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const granularityParam = (filters) => (
    filters.granularity && filters.granularity !== 'month' ? { granularity: filters.granularity } : {}
);

// The same check as the API, so the admin hears about it before a request.
export const dayRangeError = (filters) => {
    if (filters.granularity !== 'day') return null;
    const days = filters.from && filters.to
        ? (new Date(filters.to) - new Date(filters.from)) / DAY_MS + 1
        : Infinity;
    return days > MAX_DAY_BUCKETS
        ? `The day view needs a from and to date at most ${MAX_DAY_BUCKETS} days apart. Use week or month for longer ranges.`
        : null;
};

// "Month", "Week", "Day".
export const periodLabel = (granularity) => (
    GRANULARITY_OPTIONS.find((o) => o.value === granularity)?.label || 'Month'
);

// "2 PM", "12 AM".
export const hourLabel = (hour) => `${hour % 12 || 12} ${hour < 12 ? 'AM' : 'PM'}`;

export const formatDollars = (amount) => (amount == null ? '—' : `$${Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`);
export const formatNumber = (n) => (n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }));
export const formatPct = (n) => (n == null ? '—' : `${formatNumber(n)}%`);

export const commissaryLabel = (row) => {
    if (row.commissary) {
        const place = [row.commissary.city, row.commissary.state].filter(Boolean).join(', ');
        return place ? `${row.commissary.name} (${place})` : row.commissary.name;
    }
    // A matched base that's since been deleted still has an id.
    return row.commissaryBaseId ? 'Removed base' : 'Unknown';
};

export const stateLabel = (state) => state || 'Unknown';

// A byMember row's member is null once the account's been deleted.
export const memberLabel = (row) => (row.member ? memberDisplayName(row.member) : 'Deleted member');

// The scope query params for a filter set: everything but status, which the
// receipts queue carries separately.
export const scopeParams = (filters) => {
    const scope = {};
    if (filters.from) scope.from = filters.from;
    if (filters.to) scope.to = filters.to;
    if (filters.commissaryBaseId) scope.commissaryBaseId = filters.commissaryBaseId;
    if (filters.state) scope.state = filters.state;
    if (filters.member) scope.userId = filters.member._id;
    if (filters.tender) scope.tender = filters.tender;
    return scope;
};

// Product names are OCR'd receipt text, so text cells get the same
// leading-quote guard against formula injection as User.js. Numbers are left
// alone so negative amounts stay numeric.
export const csvCell = (value) => {
    let text = value == null ? '' : String(value);
    if (typeof value === 'string' && /^[=+\-@\t\r]/.test(text)) text = `'${text}`;
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const GROUP_COLUMNS = [
    ['Receipts', 'receiptCount'],
    ['Members', 'memberCount'],
    ['Total spend', 'totalSpend'],
    ['Avg receipt', 'avgReceiptValue'],
    ['Total SNAP', 'totalSnap'],
    ['Total WIC', 'totalWic'],
    ['SNAP receipts', 'snapReceiptCount'],
    ['WIC receipts', 'wicReceiptCount'],
    ['Total tax', 'totalTax'],
    ['Avg receipts per member', 'avgReceiptsPerMember'],
    ['Avg spend per member', 'avgSpendPerMember'],
    ['Location mismatches', 'locationMismatchCount'],
    ['Location mismatch %', 'locationMismatchPct'],
];

export const buildInsightsCsv = (data) => {
    const rows = [['Section', 'Group', ...GROUP_COLUMNS.map(([label]) => label)]];
    const groupRow = (section, group, row) => [section, group, ...GROUP_COLUMNS.map(([, key]) => row[key])];
    rows.push(groupRow('Totals', 'All', data.totals));
    // An API from before #799 sends none.
    (data.tenderSegments || []).forEach((row) => rows.push(groupRow('Paid with', segmentLabel(row.segment), row)));
    data.byCommissary.forEach((row) => rows.push(groupRow('Commissary', commissaryLabel(row), row)));
    data.byState.forEach((row) => rows.push(groupRow('State', stateLabel(row.state), row)));
    data.byMonth.forEach((row) => rows.push(groupRow('Month', row.month, row)));
    // An API from before #787 sends none of these.
    if (data.byPeriod && data.granularity !== 'month') {
        data.byPeriod.forEach((row) => rows.push(groupRow(periodLabel(data.granularity), row.period, row)));
    }
    (data.byHour || []).forEach((row) => rows.push(groupRow('Hour', hourLabel(row.hour), row)));
    (data.byDayOfWeek || []).forEach((row) => rows.push(groupRow('Day of week', DAY_NAMES[row.dayOfWeek - 1], row)));
    rows.push([]);
    rows.push(['Product', 'SKU / UPC', 'Code type', 'Receipts', 'Members', 'Lines', 'Quantity', 'Total spend']);
    data.topProducts.forEach((p) => rows.push([
        p.product, p.productCode, p.codeType, p.receiptCount, p.memberCount, p.lineCount, p.totalQuantity, p.totalSpend,
    ]));
    // An API from before the member breakdown sends no byMember at all.
    if (data.byMember) {
        rows.push([]);
        rows.push(['Member', 'Email', 'Receipts', 'Total spend', 'Avg receipt', 'Total SNAP', 'Location mismatches']);
        data.byMember.forEach((m) => rows.push([
            memberLabel(m), m.member?.email, m.receiptCount, m.totalSpend, m.avgReceiptValue, m.totalSnap, m.locationMismatchCount,
        ]));
    }
    return rows.map((row) => row.map(csvCell).join(',')).join('\n');
};

// ---- Product codes (somo-circle1-api#800) ----
// A product row is its printed UPC/PLU (productCode) when it has one, and
// its printed name otherwise; product is the name it's most often printed as.

// The receipts-queue scope for a product row: by code when it has one, so
// every printed name of that code is included. A row with no code counts
// only the uncoded lines printed with its name, so uncoded keeps its
// receipts to those too.
export const productScope = (row) => (row.productCode
    ? { productCode: row.productCode }
    : { product: row.product, uncoded: 'true' });

// Product Insights' selection for a product row: codes [{ code, name,
// codeType, displayCode }] or product names.
export const productSelection = (row) => (row.productCode
    ? {
        products: [],
        codes: [{
            code: row.productCode, name: row.product, codeType: row.codeType, displayCode: row.displayCode,
        }],
    }
    : { products: [row.product], codes: [] });

// The code as people read it. The API sends displayCode ("00000000040110"
// is banana PLU 4011); before it did, a PLU just lost its padding zeros.
export const formatProductCode = ({ productCode, codeType, displayCode }) => {
    if (!productCode) return '';
    if (displayCode) return displayCode;
    return codeType === 'plu' ? productCode.replace(/^0+/, '') : productCode;
};

// The SKU / UPC cell: the code, "PLU" for produce, and the other names it
// was printed as on hover. "—" for a product with no printed code.
export function ProductCodeCell({ row }) {
    if (!row.productCode) return <span className="text-muted">—</span>;
    const also = (row.printedNames || []).filter((name) => name !== row.product);
    return (
        <span title={also.length ? `Also printed as: ${also.join('; ')}` : undefined}>
            <span className="text-monospace">{formatProductCode(row)}</span>
            {row.codeType === 'plu' && <span className="badge badge-light border ml-1" title="Produce PLU, not a brand's UPC">PLU</span>}
            {also.length > 0 && <small className="text-muted ml-1">{`+${also.length} name${also.length === 1 ? '' : 's'}`}</small>}
        </span>
    );
}

// tenderSegments rows: snap, wic, neither.
export const segmentLabel = (segment) => ({ snap: 'SNAP', wic: 'WIC', neither: 'Neither' }[segment] || segment);

// SNAP / WIC / neither shoppers (somo-circle1-api#799). A segment's name
// narrows the page to it (unless a tender filter is already applied), and
// Receipts opens the queue on those receipts; a row whose linkState is null
// has no Receipts link. WIC only covers receipts that have been checked for
// WIC, so until the backfill reaches the older ones the card says how many
// that is.
export function TenderSegments({
    segments, coverage, onSelect, linkState,
}) {
    if (!segments) return null;
    const partial = coverage && coverage.checkedReceiptCount < coverage.receiptCount;
    return (
        <div className="card mb-3" data-testid="insights-tender-segments">
            <div className="card-header">
                <h3 className="card-title">SNAP &amp; WIC shoppers</h3>
                <small className="text-muted ml-2">
                    a receipt can be both SNAP and WIC
                    {partial && `; WIC checked on ${formatNumber(coverage.checkedReceiptCount)} of ${formatNumber(coverage.receiptCount)} receipts (${formatPct(coverage.checkedPct)})`}
                </small>
            </div>
            <div className="card-body table-responsive p-0">
                <table className="table table-striped mb-0 txt_lft">
                    <thead>
                        <tr>
                            <th>Paid with</th>
                            <th>Receipts</th>
                            <th>Members</th>
                            <th>Total spend</th>
                            <th>Avg receipt</th>
                            <th>Avg spend per member</th>
                            <th>Paid with SNAP / WIC</th>
                            <th aria-label="Actions" />
                        </tr>
                    </thead>
                    <tbody>
                        {segments.map((row) => {
                            const tenderAmount = { snap: row.totalSnap, wic: row.totalWic }[row.segment];
                            const receiptsState = linkState(row.segment);
                            return (
                                <tr key={row.segment}>
                                    <td className="fwt_600">
                                        {onSelect ? (
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 text-left fwt_600"
                                                title="Filter the page to this"
                                                onClick={() => onSelect(row.segment)}
                                            >
                                                {segmentLabel(row.segment)}
                                            </button>
                                        ) : segmentLabel(row.segment)}
                                    </td>
                                    <td>
                                        {formatNumber(row.receiptCount)}
                                        <small className="text-muted"> ({formatPct(row.receiptPct)})</small>
                                    </td>
                                    <td>
                                        {formatNumber(row.memberCount)}
                                        <small className="text-muted"> ({formatPct(row.memberPct)})</small>
                                    </td>
                                    <td>{formatDollars(row.totalSpend)}</td>
                                    <td>{formatDollars(row.avgReceiptValue)}</td>
                                    <td>{formatDollars(row.avgSpendPerMember)}</td>
                                    <td>
                                        {tenderAmount == null ? '—' : formatDollars(tenderAmount)}
                                        {row.tenderSharePct != null && (
                                            <small className="text-muted"> ({formatPct(row.tenderSharePct)} of spend)</small>
                                        )}
                                    </td>
                                    <td className="text-right">
                                        {receiptsState && <Link to="/loyaltyReceipts" state={receiptsState}>Receipts</Link>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// One page of rows, back to page 1 whenever the rows change (new figures
// loaded). Keyed on the array itself, so no effect is needed to reset it.
// Pass the response's own arrays (or NO_ROWS): an array built inline with
// .filter()/.map() is new every render and would pin the table to page 1.
export function usePaged(rows) {
    const [state, setState] = useState({ rows, page: 1 });
    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const page = state.rows === rows ? Math.min(state.page, totalPages) : 1;
    return {
        page,
        totalPages,
        total: rows.length,
        pageRows: rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        setPage: (next) => setState({ rows, page: next }),
    };
}

// Page numbers to show: the first, the last, and two either side of the
// current one, with null marking a gap.
const pageWindow = (page, totalPages) => {
    const pages = [];
    for (let p = 1; p <= totalPages; p += 1) {
        if (p === 1 || p === totalPages || Math.abs(p - page) <= 2) pages.push(p);
        else if (pages[pages.length - 1] !== null) pages.push(null);
    }
    return pages;
};

// pageSize is for tables paged by the API rather than by usePaged.
export function TablePager({
    page, totalPages, total, setPage, label, pageSize = PAGE_SIZE,
}) {
    if (totalPages <= 1) return null;
    const first = (page - 1) * pageSize + 1;
    const last = Math.min(page * pageSize, total);
    return (
        <div className="card-footer d-flex flex-wrap align-items-center justify-content-between" style={{ gap: '8px' }}>
            <small className="text-muted">
                {`${formatNumber(first)}–${formatNumber(last)} of ${formatNumber(total)}`}
            </small>
            <nav aria-label={`${label} pages`}>
                <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                        <button type="button" className="page-link" onClick={() => setPage(page - 1)} disabled={page === 1}>
                            Previous
                        </button>
                    </li>
                    {pageWindow(page, totalPages).map((p, i) => (p === null ? (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={`gap-${i}`} className="page-item disabled"><span className="page-link">…</span></li>
                    ) : (
                        <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                            <button type="button" className="page-link" onClick={() => setPage(p)} aria-current={p === page ? 'page' : undefined}>
                                {p}
                            </button>
                        </li>
                    )))}
                    <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                        <button type="button" className="page-link" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                            Next
                        </button>
                    </li>
                </ul>
            </nav>
        </div>
    );
}

// Vertical bars from plain CSS, like the trend table's. columns:
// [{ key, label, value, title }]; every column is drawn, zeros included.
export function ColumnChart({
    title, subtitle, columns, testId,
}) {
    const max = Math.max(0, ...columns.map((c) => c.value || 0));
    return (
        <div className="card mb-3 h-100" data-testid={testId}>
            <div className="card-header">
                <h3 className="card-title">{title}</h3>
                {subtitle && <small className="text-muted ml-2">{subtitle}</small>}
            </div>
            <div className="card-body">
                <div className="d-flex align-items-end" style={{ height: '140px', gap: '2px' }}>
                    {columns.map((c) => (
                        <div
                            key={c.key}
                            title={c.title}
                            aria-label={c.title}
                            role="img"
                            className="flex-fill d-flex flex-column justify-content-end h-100"
                        >
                            <div
                                style={{
                                    height: `${max ? Math.round(((c.value || 0) / max) * 100) : 0}%`,
                                    minHeight: c.value ? '2px' : 0,
                                    background: 'var(--primary)',
                                    borderRadius: '2px 2px 0 0',
                                }}
                            />
                        </div>
                    ))}
                </div>
                <div className="d-flex" style={{ gap: '2px' }} aria-hidden="true">
                    {columns.map((c) => (
                        <small key={c.key} className="flex-fill text-center text-muted" style={{ fontSize: '0.65rem', minWidth: 0 }}>
                            {c.label}
                        </small>
                    ))}
                </div>
            </div>
        </div>
    );
}

// Receipts by hour of day and by day of week (somo-circle1-api#787). Only
// receipts with a printed time (hour) or date (day) count, so each says how
// many it's based on. Renders nothing for an API from before #787.
export function TimeCharts({ data, totalReceipts }) {
    if (!data.byHour || !data.byDayOfWeek) return null;
    const count = (rows, key, value) => rows.find((row) => row[key] === value)?.receiptCount || 0;
    const basedOn = (n) => `based on ${formatNumber(n)} of ${formatNumber(totalReceipts)} receipts`;
    const receipts = (n) => `${formatNumber(n)} receipt${n === 1 ? '' : 's'}`;
    return (
        <div className="row">
            <div className="col-xl-8 mb-3">
                <ColumnChart
                    title="Time of day"
                    subtitle={`${basedOn(data.timeCoverage?.hourReceiptCount ?? 0)} with a printed time`}
                    testId="insights-by-hour"
                    columns={Array.from({ length: 24 }, (_, hour) => {
                        const n = count(data.byHour, 'hour', hour);
                        return {
                            key: hour,
                            // Every third hour, so the labels fit.
                            label: hour % 3 === 0 ? hourLabel(hour) : '',
                            value: n,
                            title: `${hourLabel(hour)}: ${receipts(n)}`,
                        };
                    })}
                />
            </div>
            <div className="col-xl-4 mb-3">
                <ColumnChart
                    title="Day of week"
                    subtitle={`${basedOn(data.timeCoverage?.dayOfWeekReceiptCount ?? 0)} with a printed date`}
                    testId="insights-by-day-of-week"
                    columns={DAY_NAMES.map((name, i) => {
                        const n = count(data.byDayOfWeek, 'dayOfWeek', i + 1);
                        return {
                            key: name, label: name, value: n, title: `${name}: ${receipts(n)}`,
                        };
                    })}
                />
            </div>
        </div>
    );
}

// With linkState, the whole card opens the receipts queue on click; with
// onClick, it runs that instead.
export function StatCard({
    label, value, hint, linkState, onClick, title,
}) {
    const card = (
        <div className="card h-100 mb-0 insights-stat-card">
            <div className="card-body py-3">
                <div className="text-muted small">{label}</div>
                <div className="h4 mb-0 fwt_600">{value}</div>
                {hint && <small className="text-muted">{hint}</small>}
            </div>
        </div>
    );
    return (
        <div className="col-6 col-md-4 col-xl-3 mb-3">
            {linkState && (
                <Link to="/loyaltyReceipts" state={linkState} title={title} className="insights-stat-link d-block h-100">
                    {card}
                </Link>
            )}
            {/* Not a <button>: the card's block <div>s aren't valid inside one. */}
            {!linkState && onClick && (
                <div
                    role="button"
                    tabIndex={0}
                    onClick={onClick}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onClick();
                        }
                    }}
                    title={title}
                    className="insights-stat-link h-100"
                >
                    {card}
                </div>
            )}
            {!linkState && !onClick && card}
        </div>
    );
}

function LoyaltyInsights() {
    const location = useLocation();
    // "Back to Receipt Insights" on the receipts queue hands back the filters
    // the drill-down was opened with; otherwise start unfiltered.
    const [initialFilters] = useState(() => ({ ...EMPTY_FILTERS, ...location.state?.restoreFilters }));
    const [allowed, setAllowed] = useState(null); // null = still checking
    const [filters, setFilters] = useState(initialFilters);
    const [applied, setApplied] = useState(initialFilters);
    // Remounts the member picker so Reset clears its text too.
    const [memberFieldKey, setMemberFieldKey] = useState(0);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    // Commissary/state dropdown choices: the ones any receipt has, from one
    // unfiltered call on load.
    const [options, setOptions] = useState({ commissaries: [], states: [] });

    const fetchInsights = useCallback(async (params) => {
        const res = await authManager.authenticatedFetch(
            `${Base_Url}/admin/loyalty/receipts/insights?${new URLSearchParams(params)}`,
        );
        // A gateway timeout's body is HTML or empty, not JSON.
        const body = await res.json().catch(() => null);
        if (!res.ok || body?.status !== 'success') {
            throw new Error(body?.message || 'Failed to load receipt insights.');
        }
        return body.data;
    }, []);

    // Only the latest request may update the page: an older, slower one (the
    // unfiltered first load, say) must not overwrite a newer filter's figures.
    const latestRequestRef = useRef(0);
    // Leaving the page abandons any request still running, so a late response
    // can't update state or navigate back here from wherever the admin went.
    useEffect(() => () => { latestRequestRef.current += 1; }, []);
    // The Members card scrolls here.
    const byMemberRef = useRef(null);
    // In a ref so load (and the mount effect that depends on it) stays stable.
    const navigate = useNavigate();
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;

    const load = useCallback(async (next) => {
        const requestId = ++latestRequestRef.current;
        setLoading(true);
        try {
            const result = await fetchInsights({
                ...scopeParams(next),
                status: next.status,
                ...granularityParam(next),
                topProducts: TOP_PRODUCTS,
                topMembers: TOP_MEMBERS,
            });
            if (requestId !== latestRequestRef.current) return;
            // Set together, so the drill-down links and labels always match
            // the figures on screen — a failed request leaves both as they were.
            setApplied(next);
            setData(result);
            // Keep this history entry on the filters now shown, so a refresh
            // or browser Back restores them rather than the hand-off ones.
            navigateRef.current('.', { replace: true, state: { restoreFilters: next } });
        } catch (err) {
            if (requestId !== latestRequestRef.current) return;
            console.error('Error fetching receipt insights:', err);
            toast.error(err.message || 'Failed to load receipt insights.');
        } finally {
            if (requestId === latestRequestRef.current) setLoading(false);
        }
    }, [fetchInsights]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        (async () => {
            const currentUser = await authManager.validateUser().catch(() => null);
            if (!isNonMmlAdmin(currentUser)) {
                setAllowed(false);
                setLoading(false);
                return;
            }
            setAllowed(true);
            load(initialFilters);
            fetchInsights({ status: 'all', topProducts: 0, topMembers: 0 })
                .then((all) => setOptions({
                    commissaries: all.byCommissary.filter((row) => row.commissaryBaseId),
                    states: all.byState.map((row) => row.state).filter(Boolean).sort(),
                }))
                .catch((err) => console.error('Error loading insights filter options:', err));
        })();
    }, [load, fetchInsights, initialFilters]);

    const setFilter = (field) => (e) => setFilters((prev) => ({ ...prev, [field]: e.target.value }));

    const handleApply = (e) => {
        e.preventDefault();
        if (filters.from && filters.to && filters.from > filters.to) {
            toast.error('The "from" date must be on or before the "to" date.');
            return;
        }
        const dayError = dayRangeError(filters);
        if (dayError) {
            toast.error(dayError);
            return;
        }
        load(filters);
    };

    const handleReset = () => {
        setFilters(EMPTY_FILTERS);
        setMemberFieldKey((k) => k + 1);
        load(EMPTY_FILTERS);
    };

    // Clicking a commissary or state row narrows everything to it.
    const applyGroupFilter = (field, value) => {
        const next = { ...applied, [field]: value };
        setFilters(next);
        // Drops anything typed but not applied, the member picker included.
        setMemberFieldKey((k) => k + 1);
        load(next);
    };

    // Clicking a member row narrows everything to that member, and shows them
    // in the member picker.
    const applyMemberFilter = (member) => {
        const next = { ...applied, member };
        setFilters(next);
        setMemberFieldKey((k) => k + 1);
        load(next);
    };

    // Opens the receipts queue on exactly the receipts a figure came from,
    // through its existing restoreFilters hand-off (LoyaltyReceipts.js).
    const receiptsLinkState = (extraScope = {}, label = '') => ({
        restoreFilters: {
            page: 1,
            status: applied.status,
            source: 'all',
            name: '',
            email: '',
            scope: { ...scopeParams(applied), ...extraScope },
            scopeLabel: label,
            // For the queue's "Back to Receipt Insights".
            insights: applied,
        },
    });

    const handleExport = () => {
        if (!data) return;
        const blob = new Blob([buildInsightsCsv(data)], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `receipt-insights-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // Hooks, so above the early return.
    // The trend: byPeriod for the chosen granularity, or byMonth from an API
    // from before somo-circle1-api#787.
    const trendRows = data?.byPeriod || data?.byMonth || NO_ROWS;
    const monthPages = usePaged(trendRows);
    const productPages = usePaged(data?.topProducts || NO_ROWS);
    const byMember = data?.byMember || NO_ROWS;
    const memberPages = usePaged(byMember);

    if (allowed === false) return <NotAvailable />;

    const totals = data?.totals;
    const maxMonthSpend = Math.max(0, ...trendRows.map((m) => m.totalSpend || 0));
    const trendLabel = periodLabel(data?.byPeriod ? data.granularity : 'month');
    const trendTitle = `By ${trendLabel.toLowerCase()}`;

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">Receipt Insights</h1>
                        </div>
                        <div className="col-sm-6 text-sm-right">
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={handleExport}
                                disabled={!data || loading}
                            >
                                <i className="fa-solid fa-download" /> Export CSV
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="card mb-3">
                        <form className="card-body" onSubmit={handleApply}>
                            <div className="row align-items-end">
                                <div className="col-md-2">
                                    <label htmlFor="insights-from">From</label>
                                    <input id="insights-from" type="date" className="form-control" value={filters.from} onChange={setFilter('from')} />
                                </div>
                                <div className="col-md-2">
                                    <label htmlFor="insights-to">To</label>
                                    <input id="insights-to" type="date" className="form-control" value={filters.to} onChange={setFilter('to')} />
                                </div>
                                <div className="col-md-3">
                                    <label htmlFor="insights-commissary">Commissary</label>
                                    <select id="insights-commissary" className="form-control" value={filters.commissaryBaseId} onChange={setFilter('commissaryBaseId')}>
                                        <option value="">All commissaries</option>
                                        {options.commissaries.map((row) => (
                                            <option key={row.commissaryBaseId} value={row.commissaryBaseId}>{commissaryLabel(row)}</option>
                                        ))}
                                        <option value={UNKNOWN}>Unknown (not matched)</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label htmlFor="insights-state">State</label>
                                    <select id="insights-state" className="form-control" value={filters.state} onChange={setFilter('state')}>
                                        <option value="">All states</option>
                                        {options.states.map((s) => <option key={s} value={s}>{s}</option>)}
                                        <option value={UNKNOWN}>Unknown</option>
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label htmlFor="insights-status">Status</label>
                                    <select id="insights-status" className="form-control" value={filters.status} onChange={setFilter('status')}>
                                        {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="row align-items-start mt-3">
                                <div className="col-md-2">
                                    <label htmlFor="insights-granularity">Trend by</label>
                                    <select id="insights-granularity" className="form-control" value={filters.granularity} onChange={setFilter('granularity')}>
                                        {GRANULARITY_OPTIONS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label htmlFor="insights-tender">Paid with</label>
                                    <select id="insights-tender" className="form-control" value={filters.tender} onChange={setFilter('tender')}>
                                        {TENDER_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-5">
                                    <MemberSearchField
                                        key={memberFieldKey}
                                        initialMember={filters.member}
                                        required={false}
                                        onSelect={(member) => setFilters((prev) => ({ ...prev, member }))}
                                    />
                                </div>
                                <div className="col-md-3 d-flex" style={{ gap: '8px' }}>
                                    <button
                                        type="submit"
                                        className="btn btn-primary flex-fill"
                                        style={{ backgroundColor: 'var(--primary)', borderColor: 'var(--primary)' }}
                                    >
                                        Apply
                                    </button>
                                    <button type="button" className="btn btn-outline-secondary flex-fill" onClick={handleReset}>
                                        Reset
                                    </button>
                                </div>
                            </div>
                            <small className="text-muted d-block">
                                Dates are the purchase date printed on the receipt, or the submission date when it couldn&apos;t be read.
                                State is the commissary&apos;s state. Reviewed receipts use the amounts the reviewer entered.
                            </small>
                        </form>
                    </div>

                    {loading && (
                        <div className="text-center p-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    )}

                    {!loading && data && totals.receiptCount === 0 && (
                        <div className="card mb-3">
                            <div className="card-body text-center py-5">
                                <h5>No receipts match these filters.</h5>
                            </div>
                        </div>
                    )}

                    {!loading && data && totals.receiptCount > 0 && (
                        <>
                            <div className="row" data-testid="insights-totals">
                                <StatCard
                                    label="Receipts"
                                    value={formatNumber(totals.receiptCount)}
                                    linkState={receiptsLinkState()}
                                    title="View these receipts"
                                />
                                <StatCard
                                    label="Members"
                                    value={formatNumber(totals.memberCount)}
                                    onClick={byMember.length ? () => byMemberRef.current?.scrollIntoView({ behavior: 'smooth' }) : null}
                                    title="Show the members"
                                />
                                <StatCard label="Total spend" value={formatDollars(totals.totalSpend)} />
                                <StatCard label="Avg receipt" value={formatDollars(totals.avgReceiptValue)} />
                                <StatCard label="Total SNAP" value={formatDollars(totals.totalSnap)} />
                                {totals.totalWic !== undefined && (
                                    <StatCard label="Total WIC" value={formatDollars(totals.totalWic)} hint="where the amount was read" />
                                )}
                                <StatCard label="Total tax" value={formatDollars(totals.totalTax)} />
                                <StatCard label="Avg receipts per member" value={formatNumber(totals.avgReceiptsPerMember)} />
                                <StatCard label="Avg spend per member" value={formatDollars(totals.avgSpendPerMember)} />
                                <StatCard label="Avg items per receipt" value={formatNumber(totals.avgLineItemsPerReceipt)} />
                                <StatCard
                                    label="Out-of-state shops"
                                    value={formatNumber(totals.locationMismatchCount)}
                                    hint={`${formatPct(totals.locationMismatchPct)} of receipts with both states known`}
                                />
                            </div>

                            <TenderSegments
                                segments={data.tenderSegments}
                                coverage={data.wicCoverage}
                                onSelect={applied.tender ? null : (segment) => applyGroupFilter('tender', segment)}
                                // Under a Paid with filter a row is its overlap with the filter,
                                // which the queue can't be asked for (there's no "SNAP and WIC"),
                                // so only the row that is the filter itself links. The queue
                                // banner names the tender from the scope, so no label here.
                                linkState={(segment) => (!applied.tender || applied.tender === segment
                                    ? receiptsLinkState({ tender: segment })
                                    : null)}
                            />

                            <div className="card mb-3">
                                <div className="card-header"><h3 className="card-title">{trendTitle}</h3></div>
                                <div className="card-body table-responsive p-0">
                                    <table className="table table-striped mb-0 txt_lft">
                                        <thead>
                                            <tr>
                                                <th>{trendLabel}</th>
                                                <th>Receipts</th>
                                                <th>Members</th>
                                                <th>Avg receipt</th>
                                                <th style={{ width: '45%' }}>Total spend</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {monthPages.pageRows.map((m) => (
                                                <tr key={m.period || m.month}>
                                                    <td>{m.period || m.month}</td>
                                                    <td>{formatNumber(m.receiptCount)}</td>
                                                    <td>{formatNumber(m.memberCount)}</td>
                                                    <td>{formatDollars(m.avgReceiptValue)}</td>
                                                    <td>
                                                        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                                            <div
                                                                aria-hidden="true"
                                                                style={{
                                                                    height: '10px',
                                                                    borderRadius: '2px',
                                                                    background: 'var(--primary)',
                                                                    width: `${maxMonthSpend ? Math.round(((m.totalSpend || 0) / maxMonthSpend) * 70) : 0}%`,
                                                                }}
                                                            />
                                                            <span>{formatDollars(m.totalSpend)}</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <TablePager {...monthPages} label={trendTitle} />
                            </div>

                            <TimeCharts data={data} totalReceipts={totals.receiptCount} />

                            <div className="row">
                                <div className="col-xl-7">
                                    <GroupTable
                                        title="By commissary"
                                        groupHeader="Commissary"
                                        rows={data.byCommissary}
                                        rowKey={(row) => row.commissaryBaseId || UNKNOWN}
                                        rowLabel={commissaryLabel}
                                        onSelect={applied.commissaryBaseId ? null : (row) => applyGroupFilter('commissaryBaseId', row.commissaryBaseId || UNKNOWN)}
                                        linkState={(row) => receiptsLinkState(
                                            { commissaryBaseId: row.commissaryBaseId || UNKNOWN },
                                            `Commissary: ${commissaryLabel(row)}`,
                                        )}
                                    />
                                </div>
                                <div className="col-xl-5">
                                    <GroupTable
                                        title="By state"
                                        groupHeader="State"
                                        rows={data.byState}
                                        rowKey={(row) => row.state || UNKNOWN}
                                        rowLabel={(row) => stateLabel(row.state)}
                                        onSelect={applied.state ? null : (row) => applyGroupFilter('state', row.state || UNKNOWN)}
                                        linkState={(row) => receiptsLinkState(
                                            { state: row.state || UNKNOWN },
                                            `State: ${stateLabel(row.state)}`,
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="card mb-3">
                                <div className="card-header">
                                    <h3 className="card-title">Top products</h3>
                                    <small className="text-muted ml-2">by how many receipts they appear on; grouped by SKU/UPC where one is printed</small>
                                </div>
                                <div className="card-body table-responsive p-0">
                                    {data.topProducts.length === 0 ? (
                                        <p className="text-muted p-3 mb-0">No line items were read from these receipts.</p>
                                    ) : (
                                        <table className="table table-striped mb-0 txt_lft">
                                            <thead>
                                                <tr>
                                                    <th>Product</th>
                                                    <th>SKU / UPC</th>
                                                    <th>Receipts</th>
                                                    <th>Members</th>
                                                    <th>Quantity</th>
                                                    <th>Total spend</th>
                                                    <th aria-label="Actions" />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {productPages.pageRows.map((p) => (
                                                    <tr key={p.productCode || `name:${p.product}`}>
                                                        <td className="fwt_600">
                                                            {/* Product Insights (#194), with this page's filters. */}
                                                            <Link
                                                                to="/loyaltyProductInsights"
                                                                state={{ restoreFilters: { ...applied, ...productSelection(p) } }}
                                                                title="Product insights for this product"
                                                            >
                                                                {p.product}
                                                            </Link>
                                                        </td>
                                                        <td><ProductCodeCell row={p} /></td>
                                                        <td>{formatNumber(p.receiptCount)}</td>
                                                        <td>{formatNumber(p.memberCount)}</td>
                                                        <td>{formatNumber(p.totalQuantity)}</td>
                                                        <td>{formatDollars(p.totalSpend)}</td>
                                                        <td className="text-right">
                                                            <Link to="/loyaltyReceipts" state={receiptsLinkState(productScope(p), `Product: ${p.product}`)}>
                                                                View receipts
                                                            </Link>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                <TablePager {...productPages} label="Top products" />
                            </div>
                            {byMember.length > 0 && (
                                <div className="card mb-3" ref={byMemberRef} data-testid="insights-by-member">
                                    <div className="card-header">
                                        <h3 className="card-title">By member</h3>
                                        <small className="text-muted ml-2">
                                            {byMember.length < totals.memberCount
                                                ? `the ${formatNumber(byMember.length)} with the most receipts, of ${formatNumber(totals.memberCount)}`
                                                : 'most receipts first'}
                                        </small>
                                    </div>
                                    <div className="card-body table-responsive p-0">
                                        <table className="table table-striped mb-0 txt_lft">
                                            <thead>
                                                <tr>
                                                    <th>Member</th>
                                                    <th>Email</th>
                                                    <th>Receipts</th>
                                                    <th>Total spend</th>
                                                    <th>Avg receipt</th>
                                                    <th>Out-of-state</th>
                                                    <th aria-label="Actions" />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {memberPages.pageRows.map((m) => (
                                                    <tr key={m.userId}>
                                                        <td>
                                                            {/* The name opens the Member page (#195); the
                                                                icon narrows this page to the member. */}
                                                            {m.member ? (
                                                                <Link to={`/loyaltyMembers/${m.userId}`} title="Open this member's receipts and products">
                                                                    {memberLabel(m)}
                                                                </Link>
                                                            ) : memberLabel(m)}
                                                            {m.member && !applied.member && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link btn-sm py-0 px-1 ml-1 text-muted"
                                                                    title="Filter the page to this member"
                                                                    aria-label={`Filter the page to ${memberLabel(m)}`}
                                                                    onClick={() => applyMemberFilter(m.member)}
                                                                >
                                                                    <i className="fa-solid fa-filter" aria-hidden="true" />
                                                                </button>
                                                            )}
                                                        </td>
                                                        <td>{m.member?.email || '—'}</td>
                                                        <td>{formatNumber(m.receiptCount)}</td>
                                                        <td>{formatDollars(m.totalSpend)}</td>
                                                        <td>{formatDollars(m.avgReceiptValue)}</td>
                                                        <td>
                                                            {formatNumber(m.locationMismatchCount)}
                                                            {m.locationMismatchPct != null && <small className="text-muted"> ({formatPct(m.locationMismatchPct)})</small>}
                                                        </td>
                                                        <td className="text-right">
                                                            <Link
                                                                to="/loyaltyReceipts"
                                                                state={receiptsLinkState({ userId: m.userId }, `Member: ${memberLabel(m)}`)}
                                                            >
                                                                Receipts
                                                            </Link>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <TablePager {...memberPages} label="By member" />
                                </div>
                            )}
                            {applied.member && (
                                <small className="text-muted d-block mb-3">
                                    Showing receipts for {memberDisplayName(applied.member)} ({applied.member.email}).
                                </small>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

// showMembers=false drops the Members column, for one member's own rows
// (LoyaltyMember.js), which have no memberCount.
export function GroupTable({
    title, subtitle, groupHeader, rows, rowKey, rowLabel, onSelect, linkState, showMembers = true,
}) {
    const pages = usePaged(rows);
    return (
        <div className="card mb-3">
            <div className="card-header">
                <h3 className="card-title">{title}</h3>
                {subtitle && <small className="text-muted ml-2">{subtitle}</small>}
            </div>
            <div className="card-body table-responsive p-0">
                <table className="table table-striped mb-0 txt_lft">
                    <thead>
                        <tr>
                            <th>{groupHeader}</th>
                            <th>Receipts</th>
                            {showMembers && <th>Members</th>}
                            <th>Total spend</th>
                            <th>Avg receipt</th>
                            <th>Out-of-state</th>
                            <th aria-label="Actions" />
                        </tr>
                    </thead>
                    <tbody>
                        {pages.pageRows.map((row) => (
                            <tr key={rowKey(row)}>
                                <td>
                                    {onSelect ? (
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 text-left"
                                            title="Filter the page to this"
                                            onClick={() => onSelect(row)}
                                        >
                                            {rowLabel(row)}
                                        </button>
                                    ) : rowLabel(row)}
                                </td>
                                <td>{formatNumber(row.receiptCount)}</td>
                                {showMembers && <td>{formatNumber(row.memberCount)}</td>}
                                <td>{formatDollars(row.totalSpend)}</td>
                                <td>{formatDollars(row.avgReceiptValue)}</td>
                                <td>
                                    {formatNumber(row.locationMismatchCount)}
                                    {row.locationMismatchPct != null && <small className="text-muted"> ({formatPct(row.locationMismatchPct)})</small>}
                                </td>
                                <td className="text-right">
                                    <Link to="/loyaltyReceipts" state={linkState(row)}>Receipts</Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <TablePager {...pages} label={title} />
        </div>
    );
}

export default LoyaltyInsights;
